import React, { useState } from 'react';
import {
  Modal, Form, DatePicker, Select, Button, Space, Alert, Progress, Spin
} from 'antd';
import {
  FilePdfOutlined, FileExcelOutlined, FileOutlined, FileTextOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { notify } from 'components';
import useIsMobile from 'hooks/useIsMobile';

const { RangePicker } = DatePicker;
const { Option } = Select;

const ReportGenerationModal = ({ visible, report, onClose, onSuccess }) => {
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleGenerate = async (values) => {
    setGenerating(true);
    setProgress(20);

    try {
      // First, get or create the report configuration
      const reportsResponse = await axios.get('/reports/reports/', {
        params: {
          report_type: report.report_type,
          active_only: true
        }
      });

      let reportId;
      if (reportsResponse.data.results && reportsResponse.data.results.length > 0) {
        reportId = reportsResponse.data.results[0].id;
      } else {
        // Create report config if it doesn't exist
        const createResponse = await axios.post('/reports/reports/', {
          name: report.report_type_display,
          report_type: report.report_type,
          default_format: values.format,
          is_active: true
        });
        reportId = createResponse.data.id;
      }

      setProgress(40);

      // Generate the report
      const generateResponse = await axios.post('/reports/generations/', {
        report_id: reportId,
        start_date: values.dateRange[0].format('YYYY-MM-DD'),
        end_date: values.dateRange[1].format('YYYY-MM-DD'),
        export_format: values.format,
        filters: values.filters || {}
      });

      setProgress(100);

      if (generateResponse.status === 201) {
        const generation = generateResponse.data;

        if (generation.status === 'completed') {
          notify('success', 'Report generated successfully!');

          // Download the report
          setTimeout(async () => {
            try {
              const exportResponse = await axios.get(`/reports/exports/?generation=${generation.id}`);
              if (exportResponse.data.results && exportResponse.data.results.length > 0) {
                const exportData = exportResponse.data.results[0];

                // Trigger download
                const downloadResponse = await axios.get(
                  `/reports/exports/${exportData.id}/download/`,
                  { responseType: 'blob' }
                );

                const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', exportData.file_name);
                document.body.appendChild(link);
                link.click();
                link.remove();
              }
            } catch (error) {
              console.error('Error downloading report:', error);
            }
          }, 1000);

          onSuccess();
          form.resetFields();
        } else if (generation.status === 'failed') {
          notify('error', `Report generation failed: ${generation.error_message || 'Unknown error'}`);
        } else {
          notify('info', 'Report is being generated. Check report history for status.');
          onSuccess();
          form.resetFields();
        }
      }
    } catch (error) {
      console.error('Error generating report:', error);
      notify('error', error.response?.data?.message || 'Failed to generate report');
    } finally {
      setGenerating(false);
      setProgress(0);
    }
  };

  const getFormatIcon = (format) => {
    const icons = {
      pdf: <FilePdfOutlined style={{ color: '#f5222d' }} />,
      excel: <FileExcelOutlined style={{ color: '#52c41a' }} />,
      csv: <FileTextOutlined style={{ color: '#1890ff' }} />,
      json: <FileOutlined style={{ color: '#722ed1' }} />
    };
    return icons[format] || <FileOutlined />;
  };

  return (
    <Modal
      title={`Generate ${report?.report_type_display || 'Report'}`}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={isMobile ? '100%' : 600}
      style={isMobile ? { top: 20, maxWidth: '100vw', margin: '0 10px' } : {}}
    >
      <Alert
        message={report?.category + ' Report'}
        description={isMobile ? undefined : `Generate ${report?.report_type_display} for the selected date range and format.`}
        type="info"
        showIcon
        style={{ marginBottom: isMobile ? 16 : 24 }}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleGenerate}
        initialValues={{
          dateRange: [dayjs().subtract(30, 'days'), dayjs()],
          format: 'pdf'
        }}
        size={isMobile ? 'middle' : 'large'}
      >
        <Form.Item
          name="dateRange"
          label="Date Range"
          rules={[{ required: true, message: 'Please select date range' }]}
        >
          <RangePicker
            style={{ width: '100%' }}
            format="YYYY-MM-DD"
            disabledDate={(current) => current && current > dayjs().endOf('day')}
            size={isMobile ? 'middle' : 'large'}
          />
        </Form.Item>

        <Form.Item
          name="format"
          label="Export Format"
          rules={[{ required: true, message: 'Please select format' }]}
        >
          <Select size={isMobile ? 'middle' : 'large'}>
            <Option value="pdf">
              <Space>
                {getFormatIcon('pdf')}
                <span>{isMobile ? 'PDF' : 'PDF - Portable Document Format'}</span>
              </Space>
            </Option>
            <Option value="excel">
              <Space>
                {getFormatIcon('excel')}
                <span>{isMobile ? 'Excel (XLSX)' : 'Excel - Spreadsheet (XLSX)'}</span>
              </Space>
            </Option>
            <Option value="csv">
              <Space>
                {getFormatIcon('csv')}
                <span>{isMobile ? 'CSV' : 'CSV - Comma Separated Values'}</span>
              </Space>
            </Option>
            <Option value="json">
              <Space>
                {getFormatIcon('json')}
                <span>{isMobile ? 'JSON' : 'JSON - JavaScript Object Notation'}</span>
              </Space>
            </Option>
          </Select>
        </Form.Item>

        {generating && (
          <div style={{ marginBottom: 16 }}>
            <Progress percent={progress} status="active" size={isMobile ? 'small' : 'default'} />
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <Spin size="small" /> Generating report...
            </div>
          </div>
        )}

        <Form.Item style={{ marginBottom: 0 }}>
          <Space style={{ width: '100%', justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
            <Button onClick={onClose} disabled={generating} size={isMobile ? 'middle' : 'large'}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={generating} size={isMobile ? 'middle' : 'large'}>
              Generate
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ReportGenerationModal;
